import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import type { AdPlacement } from "@/mocks/admin";
import { adPlacements } from "@/mocks/admin";

const STORAGE_KEY = "hj_ad_placements";

function loadFromStorage(): AdPlacement[] | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as AdPlacement[];
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    // ignore
  }
  return null;
}

function saveToStorage(placements: AdPlacement[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(placements));
}

function rowToPlacement(row: Record<string, unknown>): AdPlacement {
  return {
    id: row.id as string,
    name: row.name as string,
    title: (row.title as string) || "",
    description: (row.description as string) || "",
    position: row.position as AdPlacement["position"],
    adType: row.ad_type as AdPlacement["adType"],
    device: row.device as AdPlacement["device"],
    pageType: row.page_type as AdPlacement["pageType"],
    isActive: row.is_active as boolean,
    adsensePublisherId: (row.adsense_publisher_id as string) || undefined,
    adsenseSlotId: (row.adsense_slot_id as string) || undefined,
    customHtml: (row.custom_html as string) || undefined,
    imageUrl: (row.image_url as string) || undefined,
    imageAlt: (row.image_alt as string) || undefined,
    linkUrl: (row.link_url as string) || undefined,
    width: (row.width as number) || 728,
    height: (row.height as number) || 90,
    impressions: (row.impressions as number) || 0,
    clicks: (row.clicks as number) || 0,
    createdAt: (row.created_at as string) || new Date().toISOString(),
    updatedAt: (row.updated_at as string) || new Date().toISOString(),
  };
}

function placementToRow(ad: Partial<AdPlacement>): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  if (ad.name !== undefined) row.name = ad.name;
  if (ad.title !== undefined) row.title = ad.title;
  if (ad.description !== undefined) row.description = ad.description;
  if (ad.position !== undefined) row.position = ad.position;
  if (ad.adType !== undefined) row.ad_type = ad.adType;
  if (ad.device !== undefined) row.device = ad.device;
  if (ad.pageType !== undefined) row.page_type = ad.pageType;
  if (ad.isActive !== undefined) row.is_active = ad.isActive;
  if (ad.adsensePublisherId !== undefined) row.adsense_publisher_id = ad.adsensePublisherId;
  if (ad.adsenseSlotId !== undefined) row.adsense_slot_id = ad.adsenseSlotId;
  if (ad.customHtml !== undefined) row.custom_html = ad.customHtml;
  if (ad.imageUrl !== undefined) row.image_url = ad.imageUrl;
  if (ad.imageAlt !== undefined) row.image_alt = ad.imageAlt;
  if (ad.linkUrl !== undefined) row.link_url = ad.linkUrl;
  if (ad.width !== undefined) row.width = ad.width;
  if (ad.height !== undefined) row.height = ad.height;
  if (ad.impressions !== undefined) row.impressions = ad.impressions;
  if (ad.clicks !== undefined) row.clicks = ad.clicks;
  return row;
}

export function useAdsDB() {
  const [placements, setPlacementsState] = useState<AdPlacement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSupabase, setIsSupabase] = useState(false);

  const fetchFromSupabase = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from("ad_placements")
        .select("*")
        .order("created_at", { ascending: true });

      if (dbError) {
        setError(dbError.message);
        setIsSupabase(false);
        // Fallback to localStorage
        const stored = loadFromStorage();
        if (stored) {
          setPlacementsState(stored);
        } else {
          setPlacementsState([...adPlacements]);
          saveToStorage([...adPlacements]);
        }
        return;
      }

      const items = (data ?? []).map((r) => rowToPlacement(r as Record<string, unknown>));
      setPlacementsState(items.length > 0 ? items : [...adPlacements]);
      setIsSupabase(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unbekannter Fehler");
      setIsSupabase(false);
      const stored = loadFromStorage();
      setPlacementsState(stored ?? [...adPlacements]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFromSupabase();
  }, [fetchFromSupabase]);

  const createAd = useCallback(
    async (ad: Omit<AdPlacement, "id" | "impressions" | "clicks" | "createdAt" | "updatedAt">): Promise<{ data: AdPlacement | null; error: string | null }> => {
      const now = new Date().toISOString();
      const tempId = crypto.randomUUID();
      const newAd: AdPlacement = {
        ...ad,
        id: tempId,
        impressions: 0,
        clicks: 0,
        createdAt: now,
        updatedAt: now,
      };

      if (isSupabase) {
        const row = placementToRow(newAd);
        const { data, error: dbError } = await supabase
          .from("ad_placements")
          .insert({ ...row, id: tempId })
          .select()
          .maybeSingle();
        if (dbError) {
          return { data: null, error: dbError.message };
        }
        if (data) {
          const created = rowToPlacement(data as Record<string, unknown>);
          setPlacementsState((prev) => [...prev, created]);
          return { data: created, error: null };
        }
      }

      // localStorage fallback
      const next = [...placements, newAd];
      setPlacementsState(next);
      saveToStorage(next);
      return { data: newAd, error: null };
    },
    [placements, isSupabase]
  );

  const updateAd = useCallback(
    async (id: string, updates: Partial<AdPlacement>): Promise<{ error: string | null }> => {
      const now = new Date().toISOString();

      if (isSupabase) {
        const row = placementToRow(updates);
        const { error: dbError } = await supabase
          .from("ad_placements")
          .update(row)
          .eq("id", id);
        if (dbError) {
          return { error: dbError.message };
        }
      }

      const next = placements.map((a) =>
        a.id === id
          ? { ...a, ...updates, updatedAt: now }
          : a
      );
      setPlacementsState(next);
      if (!isSupabase) saveToStorage(next);
      return { error: null };
    },
    [placements, isSupabase]
  );

  const deleteAd = useCallback(
    async (id: string): Promise<{ error: string | null }> => {
      if (isSupabase) {
        const { error: dbError } = await supabase
          .from("ad_placements")
          .delete()
          .eq("id", id);
        if (dbError) {
          return { error: dbError.message };
        }
      }

      const next = placements.filter((a) => a.id !== id);
      setPlacementsState(next);
      if (!isSupabase) saveToStorage(next);
      return { error: null };
    },
    [placements, isSupabase]
  );

  const refetch = useCallback(async () => {
    await fetchFromSupabase();
  }, [fetchFromSupabase]);

  // Sync across tabs
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        const stored = loadFromStorage();
        if (stored) setPlacementsState(stored);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  return { placements, loading, error, refetch, createAd, updateAd, deleteAd, isSupabase };
}